import { createContext, FC, useContext } from 'react';

import { WithChildren } from '../helpers';

const I18N_CONFIG_KEY = process.env.VITE_I18N_CONFIG_KEY || 'i18nConfig';

export const SUPPORTED_LOCALES = ['de', 'en', 'fr', 'it'] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'de';

/**
 * Query parameter that lets an inbound link choose the locale, e.g.
 * `/auth?sessionToken=…&lang=fr`.
 *
 * This exists for the magic-link email. That link lands before any customer data
 * has been fetched, so without it the first paint is always `DEFAULT_LOCALE` and a
 * French customer reads German until `applyPreferredLanguage` runs and reloads.
 */
const LANG_QUERY_PARAM = 'lang';

type Props = {
  selectedLang: Locale;
};

/**
 * What actually sits in localStorage.
 *
 * `explicit` records WHY this locale is stored: true when the customer chose it in
 * the dashboard switcher, false when it was applied from their backend preference.
 * Without that distinction the two are indistinguishable, and the backend value —
 * which is re-applied on every customer fetch — silently reverted the customer's
 * own choice moments after they made it.
 */
type StoredConfig = {
  selectedLang: Locale;
  explicit: boolean;
};

const initialState: Props = {
  selectedLang: DEFAULT_LOCALE,
};

const isSupportedLocale = (value: unknown): value is Locale =>
  typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);

/**
 * Reads the stored config, or nulls when there is nothing usable.
 *
 * A persisted locale can outlive the language it names (a removed language,
 * hand-edited storage). Without the guard an unknown code reaches
 * `allMessages[locale]` as undefined and every string renders as its raw key id,
 * so fall back rather than trust what is in storage.
 */
function readStored(): { selectedLang: Locale | null; explicit: boolean } {
  try {
    const ls = localStorage.getItem(I18N_CONFIG_KEY);
    if (!ls) return { selectedLang: null, explicit: false };

    const parsed = JSON.parse(ls) as Partial<StoredConfig>;
    const stored: unknown = parsed?.selectedLang;

    return {
      selectedLang: isSupportedLocale(stored) ? stored : null,
      // Anything written before `explicit` existed counts as non-explicit, so a
      // backend preference still gets its first chance to apply.
      explicit: parsed?.explicit === true,
    };
  } catch (er) {
    console.error(er);
    return { selectedLang: null, explicit: false };
  }
}

/**
 * Precedence, highest first:
 *   1. anything stored — an explicit switcher choice, a `?lang=` link, or an
 *      applied `customer.preferredLanguage`
 *   2. the device / browser language, for any supported locale (de/en/fr/it) —
 *      the first supported language the device asks for wins
 *   3. DEFAULT_LOCALE
 *
 * The device language is deliberately NOT persisted, which is why it is resolved
 * here rather than written by `adoptQueryLocale`. Storage records decisions —
 * a choice the customer made, a locale a link carried, a preference the backend
 * supplied — and a guess written alongside those becomes indistinguishable from
 * them. Left unpersisted it also keeps following the device: change the phone's
 * language and the app follows, which a stored copy would not.
 */
function getConfig(): Props {
  const stored = readStored().selectedLang;
  if (stored) return { selectedLang: stored };

  return { selectedLang: readDeviceLocale() ?? initialState.selectedLang };
}

/** Whether the customer has picked a language in the dashboard themselves. */
const hasExplicitChoice = (): boolean => {
  const stored = readStored();

  return stored.explicit && stored.selectedLang !== null;
};

const write = (lang: Locale, explicit: boolean) => {
  localStorage.setItem(I18N_CONFIG_KEY, JSON.stringify({ selectedLang: lang, explicit }));
};

const persist = (lang: Locale, explicit: boolean) => {
  write(lang, explicit);
  window.location.reload();
};

/**
 * Side effect. The customer choosing a language themselves — currently only
 * `LanguageSwitcher`. Marked explicit, so their backend preference stops
 * overriding it.
 */
export function setLanguage(lang: Locale) {
  persist(lang, true);
}

/**
 * Maps the backend's language form to this app's locale.
 *
 * petcon stores and transmits uppercase (`'DE' | 'EN' | 'FR' | 'IT'`, the form its
 * `globalAtoms.lang` uses and the form Klaviyo receives); react-intl here wants
 * lowercase. The two representations are deliberate, not an accident, so the
 * conversion lives in one place.
 */
export const localeFromPreference = (value: unknown): Locale | null => {
  if (typeof value !== 'string') return null;

  const lower = value.trim().toLowerCase();

  return isSupportedLocale(lower) ? lower : null;
};

/**
 * The locale named by `?lang=`, or null.
 *
 * Parsed through `localeFromPreference`, so `?lang=FR` works as well as `?lang=fr`
 * — whoever builds the link may interpolate petcon's uppercase form, and an email
 * template is an awkward place to discover a casing rule.
 */
const readQueryLocale = (): Locale | null => {
  if (typeof window === 'undefined') return null;

  try {
    return localeFromPreference(new URLSearchParams(window.location.search).get(LANG_QUERY_PARAM));
  } catch (er) {
    console.error(er);
    return null;
  }
};

/**
 * The first supported locale the device asks for, or null.
 *
 * This is what covers the pre-authentication screens. Login and the magic-link
 * landing page run before any customer has been fetched, so there is no
 * `preferredLanguage` to read and — unless a link carried `?lang=` — the only
 * signal available is what the browser reports.
 *
 * Reads `navigator.languages` rather than `navigator.language`: it is an ordered
 * preference list, so a device set to Spanish first and French second resolves to
 * French instead of falling back to German over a language the customer does read.
 *
 * Tags are region-qualified (`fr-FR`, `de-AT`, `en-GB`), so only the primary
 * subtag is compared — `de-AT` and `de-DE` are both German as far as this app is
 * concerned.
 */
const readDeviceLocale = (): Locale | null => {
  if (typeof navigator === 'undefined') return null;

  try {
    const requested = navigator.languages?.length
      ? navigator.languages
      : navigator.language
        ? [navigator.language]
        : [];

    for (const tag of requested) {
      const locale = localeFromPreference(tag?.split('-')[0]);

      // Languages this app has no catalogue for are skipped rather than stopping
      // the scan: a device asking for Spanish then French should land on French,
      // and bailing at Spanish would hand that customer German over a language
      // they do read.
      if (!locale) continue;

      // The first language we DO have a catalogue for settles it. The order is the
      // customer's own preference list, so an English-primary device resolves to
      // English, a French-primary one to French; a language we do not support is
      // skipped above rather than ending the scan.
      return locale;
    }

    return null;
  } catch (er) {
    console.error(er);
    return null;
  }
};

/**
 * Adopts `?lang=` into stored config. Runs once, at module load.
 *
 * At module load rather than in an effect because `MetronicI18nProvider` reads the
 * config during render: anything applied later would need a reload to be seen,
 * which is the flash this is meant to remove. And outside `getConfig` so that
 * reading the config stays free of side effects.
 *
 * Stored NON-explicit, because a link carries the customer's recorded preference
 * rather than a choice they just made — `applyPreferredLanguage` has to stay free
 * to correct it. By the same logic an existing explicit choice wins outright:
 * someone who picked French in the switcher and then opens an older link carrying
 * `lang=de` meant French.
 *
 * Persisting matters even though the value is already in the URL. The parameter
 * only exists on the landing URL, so once the app navigates it is gone and a later
 * reload would drop back to German. It also stops a second reload: with the locale
 * already stored, `applyPreferredLanguage` finds the preference and the active
 * locale in agreement and does nothing.
 *
 * No reload here — this lands before the first render, so there is nothing to
 * re-render, and reloading would re-read the same parameter forever.
 */
const adoptQueryLocale = () => {
  const fromQuery = readQueryLocale();
  if (!fromQuery) return;

  const stored = readStored();
  if (stored.explicit) return;
  if (stored.selectedLang === fromQuery) return;

  write(fromQuery, false);
};

adoptQueryLocale();

/**
 * Applies the customer's preferred language, which reaches us as
 * `customer.preferredLanguage`.
 *
 * The backend value is authoritative UNTIL the customer overrides it here. It is
 * re-applied on every customer fetch, and `fetchCustomerRequest` fires on far more
 * than boot — discounts, shop add/remove, subscription edits — so without the
 * explicit-choice guard it reverted the switcher within seconds of using it, with
 * a page reload.
 *
 * Precedence, highest first:
 *   1. An explicit choice in the dashboard switcher
 *   2. `customer.preferredLanguage` from the storefront, via petcon
 *   3. A `?lang=` link, already stored non-explicitly by `adoptQueryLocale`
 *   4. The device language (any supported locale)
 *   5. DEFAULT_LOCALE
 *
 * Compared against the ACTIVE locale rather than against "is anything stored", so
 * that it never reloads to reach a language already on screen — that would be a
 * visible flash for no change. Note the active locale with nothing stored is
 * whatever `getConfig` resolves, which since the device fallback is no longer
 * necessarily `DEFAULT_LOCALE`; comparing against the resolved value rather than
 * the constant is what keeps this correct.
 *
 * That same comparison prevents a reload loop — after the reload the stored value
 * equals the preference, so this returns false.
 *
 * The explicit choice is local to this browser. Persisting it server-side would
 * make it follow the customer across devices, and is the remaining piece of work
 * here.
 *
 * Returns whether it triggered a reload. The reload is needed because
 * `MetronicI18nProvider` reads the config during render, so the locale is fixed
 * for the page's lifetime.
 */
export const applyPreferredLanguage = (value: unknown): boolean => {
  if (hasExplicitChoice()) return false;

  const locale = localeFromPreference(value);
  if (!locale) return false;

  if (locale === getConfig().selectedLang) return false;

  persist(locale, false);

  return true;
};

/**
 * The active locale, readable outside React.
 *
 * `useLang` covers components, but `setupAxios` runs before any of them and has
 * to put the locale on every request. Safe to read once at startup rather than
 * subscribe: `setLanguage` reloads the page, so the locale cannot change during
 * a page's lifetime.
 */
export const activeLocale = (): Locale => getConfig().selectedLang;

const I18nContext = createContext<Props>(initialState);

const useLang = () => {
  return useContext(I18nContext).selectedLang;
};

const MetronicI18nProvider: FC<WithChildren> = ({ children }) => {
  const lang = getConfig();
  return <I18nContext.Provider value={lang}>{children}</I18nContext.Provider>;
};

export { MetronicI18nProvider, useLang };
